'use client'

import { useCallback, useState } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { pathOr } from 'ramda'

import { useCustomerData } from 'modules/customer'
import { PATHS } from 'shared/utils/paths'

import { ProductBadgeProps, ProductTypes } from '../types/products'
import { badgesSegregation, tagsSegregation } from '../utils/badges'
import {
  getMaxQuantityForSpecialOffer,
  getPricePerUnit,
  getProductUnitDetails
} from '../utils/products'

import useProductAgeGate from './useProductAgeGate'

const LIMIT_BADGES_DISPLAY = 3

const useProductCard = ({
  product,
  ageLimitMetCategory,
  productPath
}: {
  product: ProductTypes
  ageLimitMetCategory?: boolean
  productPath: string
}) => {
  const [hover, setHover] = useState(false)
  const [quantityJump, setQuantityJump] = useState<number>(1)
  const { user } = useCustomerData()
  const router = useRouter()
  const searchParams = useSearchParams()

  const { name, nameShort, nameDescriptionShort, tags, brand } = product

  const path = productPath[0] !== '/' ? `/${productPath}` : productPath

  const productTags = product?.tags || []
  const commercialTags = product?.commercialTags || []

  const promoPercentage = product.specialOfferPricing?.discount
  const parsedTags: string[] = tagsSegregation([
    ...productTags,
    ...commercialTags
  ])
  const unitDetails = getProductUnitDetails(product)
  const pricePerUnit = getPricePerUnit(product)
  const parsedBadges: ProductBadgeProps[] = badgesSegregation(
    tags,
    'mini'
  ).slice(0, LIMIT_BADGES_DISPLAY)

  const {
    isProductBlurred: blurProduct,
    blurProductMessage,
    loginModalOpen,
    dobModalOpen,
    handleBlurredProductClick,
    handleCloseLoginModal,
    handleCloseDobModal
  } = useProductAgeGate({ product, ageLimitMetCategory })

  const handleMouseOver = () => setHover(!blurProduct)
  const handleMouseOut = () => setHover(false)

  const productImage = pathOr(
    '/static/photo-placeholder.svg',
    ['files', 0, 'thumbnailPathMedium'],
    product
  )

  const handleBrandClick = useCallback(
    (filterValue: string | null) => {
      const current = new URLSearchParams(
        Array.from(searchParams?.entries() || [])
      )

      if (!filterValue) {
        current.delete('filter.brand_id')
      } else {
        current.set('filter.brand_id', filterValue)
      }

      const search = current.toString()
      const query = search ? `?${search}` : ''

      router.push(`${path}${query}`)
    },
    [searchParams, path, router]
  )

  const maxQuantityForSpecialOffer = getMaxQuantityForSpecialOffer(product)

  const brandPath =
    brand?.slug &&
    (brand.slug[0] !== '/'
      ? `${PATHS.brand}/${typeof brand.slug === 'object' ? brand.slug.slug : brand.slug}`
      : `${PATHS.brand}${typeof brand?.slug === 'object' ? brand?.slug.slug : brand?.slug}`)

  return {
    user,
    hover,
    quantityJump,
    setQuantityJump,
    name,
    nameShort,
    nameDescriptionShort,
    brand,
    promoPercentage,
    parsedTags,
    unitDetails,
    pricePerUnit,
    parsedBadges,
    blurProduct,
    blurProductMessage,
    handleMouseOver,
    handleMouseOut,
    loginModalOpen,
    handleCloseLoginModal,
    dobModalOpen,
    handleCloseDobModal,
    productImage,
    handleBlurredProductClick,
    handleBrandClick,
    maxQuantityForSpecialOffer,
    path,
    brandPath
  }
}

export default useProductCard
